import { EmailContentType, Locales } from "@simplyk/common";

import { TicketingTranslationData } from "../translation.types";

import {
  EditorEmailContentType,
  FormEmailContents,
  getEditorEmailText,
  getTicketingThankYouContentType,
} from "@/features/LiveFormEditor/common/formEmailContents";

export type TicketingEmailTranslationText = Pick<
  TicketingTranslationData,
  | "emailObject"
  | "emailContent"
  | "thankYouBidPlacedEmailObject"
  | "thankYouBidPlacedEmailContent"
  | "bidLoserEmailObject"
  | "bidLoserEmailContent"
  | "expiringMembershipEmailObject"
  | "expiringMembershipEmailContent"
  | "autoRenewalEmailObject"
  | "autoRenewalEmailContent"
  | "readyForPickupEmailObject"
  | "readyForPickupEmailContent"
  | "orderShippedEmailObject"
  | "orderShippedEmailContent"
> & { emailAttachmentUrl?: string };

export const buildTicketingEmailTranslationText = (
  contents: FormEmailContents,
  locale: Locales,
  isAuction: boolean
): TicketingEmailTranslationText => {
  const textOf = (type: EditorEmailContentType) => getEditorEmailText(contents, type, locale);
  const thankYou = textOf(getTicketingThankYouContentType(isAuction));
  const bidPlaced = isAuction ? textOf(EmailContentType.THANK_YOU) : undefined;
  const nonWinner = textOf(EmailContentType.AUCTION_NON_WINNERS);
  const expiring = textOf(EmailContentType.MEMBERSHIP_EXPIRATION);
  const autoRenewal = textOf(EmailContentType.MEMBERSHIP_AUTO_RENEWAL);
  const readyForPickup = textOf(EmailContentType.READY_FOR_PICKUP);
  const orderShipped = textOf(EmailContentType.ORDER_SHIPPED);

  return {
    emailObject: thankYou.subject || undefined,
    emailContent: thankYou.body || undefined,
    emailAttachmentUrl: thankYou.attachmentUrl || undefined,
    thankYouBidPlacedEmailObject: bidPlaced?.subject || undefined,
    thankYouBidPlacedEmailContent: bidPlaced?.body || undefined,
    bidLoserEmailObject: nonWinner.subject || undefined,
    bidLoserEmailContent: nonWinner.body || undefined,
    expiringMembershipEmailObject: expiring.subject || undefined,
    expiringMembershipEmailContent: expiring.body || undefined,
    autoRenewalEmailObject: autoRenewal.subject || undefined,
    autoRenewalEmailContent: autoRenewal.body || undefined,
    readyForPickupEmailObject: readyForPickup.subject || undefined,
    readyForPickupEmailContent: readyForPickup.body || undefined,
    orderShippedEmailObject: orderShipped.subject || undefined,
    orderShippedEmailContent: orderShipped.body || undefined,
  };
};

export interface DonationEmailTranslationText {
  emailObject: string | null;
  emailContent: string | null;
  emailAttachmentUrl: string | null;
  autoRenewalEmailObject: string | null;
  autoRenewalEmailContent: string | null;
}

export const buildDonationEmailTranslationText = (
  contents: FormEmailContents,
  locale: Locales
): DonationEmailTranslationText => {
  const thankYou = getEditorEmailText(contents, EmailContentType.THANK_YOU, locale);
  const autoRenewal = getEditorEmailText(contents, EmailContentType.MEMBERSHIP_AUTO_RENEWAL, locale);

  return {
    emailObject: thankYou.subject,
    emailContent: thankYou.body,
    emailAttachmentUrl: thankYou.attachmentUrl,
    autoRenewalEmailObject: autoRenewal.subject,
    autoRenewalEmailContent: autoRenewal.body,
  };
};
